import React, { useEffect, useState, useMemo } from "react";
import "./worker_my_hours.css"; 
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* ===== FIREBASE ===== */
import { db } from "../firebase"; 
import { doc, onSnapshot } from "firebase/firestore";

/* ===== HELPERS ===== */
const timeToMin = (str) => {
  if (!str || !str.includes(":")) return 0;
  const [h, m] = str.split(":").map(Number);
  return h * 60 + m;
};

const minToTime = (totalMin) => {
  if (totalMin < 0) totalMin = 0;
  const h = Math.floor(totalMin / 60);
  const m = Math.floor(totalMin % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const WorkerMyHours = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const workerId = localStorage.getItem("workerId");
  const workerName = localStorage.getItem("workerName") || t('worker');

  const [loading, setLoading] = useState(true);
  const [workEntries, setWorkEntries] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const changeMonth = (offset) => {
    let newMonth = currentMonth + offset;
    let newYear = currentYear;
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };

  useEffect(() => {
    const isRegistered = localStorage.getItem("workerDeviceRegistered") === "true";
    const hasSession = localStorage.getItem("workerGateDone") === "true";

    if (!isRegistered) { navigate("/worker", { replace: true }); return; }
    if (!hasSession || !workerId) { navigate("/worker/login", { replace: true }); return; }

    const monthId = `${currentYear}_${String(currentMonth).padStart(2, "0")}`;
    const docId = `${workerId}_${monthId}`;
    const docRef = doc(db, "monthlyWorkHours", docId);

    const unsubscribe = onSnapshot(docRef, (snap) => {
      setLoading(true);
      if (snap.exists()) {
        const data = snap.data().days || {};
        const sorted = Object.entries(data)
          .map(([day, val]) => ({ day: Number(day), ...val }))
          // --- GELEŽINIS FILTRAS ---
          .filter(entry => {
              // 1. Griežtas statusų blokavimas (neleidžia praeiti jokiems laisvadieniams)
              const isForbidden = 
                entry.isStatusOnly === true || 
                entry.status === 'laisva' || 
                entry.status === 'frei' || 
                entry.status === 'krank' || 
                entry.status === 'urlaub';

              if (isForbidden) return false;
              
              // 2. Projekto patikra
              const hasProject = (entry.objekt && entry.objekt.trim() !== "") || 
                                 (entry.projectName && entry.projectName.trim() !== "");
              
              // 3. Rodome tik jei yra projektas IR įvestas pabaigos laikas (STOP)
              return hasProject && entry.end;
          })
          .sort((a, b) => b.day - a.day); 
        
        setWorkEntries(sorted);
      } else {
        setWorkEntries([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [workerId, currentMonth, currentYear, navigate]);

  const monthlyTotal = useMemo(() => {
    let totalMins = 0;
    workEntries.forEach(entry => {
      // Skaičiuojame tik realiai dirbtas valandas (dviguba patikra dėl statuso)
      if (entry.start && entry.end && entry.status !== 'laisva') {
        const diff = timeToMin(entry.end) - timeToMin(entry.start) - timeToMin(entry.pause);
        if (diff > 0) totalMins += diff;
      }
    });
    return minToTime(totalMins);
  }, [workEntries]);

  const calculateDayTotal = (entry) => {
    if (!entry.end || !entry.start) return null; 
    const diff = timeToMin(entry.end) - timeToMin(entry.start) - timeToMin(entry.pause);
    return diff > 0 ? minToTime(diff) : "00:00";
  };

  const monthLabel = new Date(currentYear, currentMonth - 1).toLocaleString(i18n.language, { month: 'long' });

  return (
    <div className="hours-page">
      <div className="hours-header">
        <button className="gold-back-btn" onClick={() => navigate(-1)}>← {t('back')}</button>
        <div className="name">{workerName}</div>
      </div>

      <div className="month-nav">
          <button className="nav-arrow-btn" onClick={() => changeMonth(-1)}>‹</button>
          <div className="month-label">
            {monthLabel} {currentYear}
          </div>
          <button className="nav-arrow-btn" onClick={() => changeMonth(1)}>›</button>
      </div>

      <div className="monthly-summary">
          <div className="sum-title">{t('total_netto')}:</div>
          <div className="sum-hours">{monthlyTotal} h</div>
      </div>

      <div className="hours-scroll">
        {loading ? (
          <div className="day-card-msg">{t('loading')}...</div>
        ) : workEntries.length === 0 ? (
          <div className="day-card-msg">{t('no_work_this_week')}</div>
        ) : (
          workEntries.map((entry, idx) => {
            const dateObj = new Date(currentYear, currentMonth - 1, entry.day);
            const dayName = dateObj.toLocaleDateString(i18n.language, { weekday: 'short' });

            return (
              <div key={idx} className="day-card">
                <div className="day-title">
                  {String(entry.day).padStart(2, '0')}.{String(currentMonth).padStart(2, '0')}.{currentYear}
                  <span style={{ marginLeft: "8px", color: "#38bdf8", textTransform: "uppercase", fontSize: "14px" }}>
                    ({dayName})
                  </span>
                </div>
                
                <div className="info-row project-row">
                  <span>{t('project')}:</span>
                  <strong>{entry.objekt || entry.projectName || "---"}</strong>
                </div>

                <div className="info-row">
                  <span>{t('workStart')}:</span>
                  <strong>{entry.start || "--:--"}</strong>
                </div>

                <div className="info-row">
                  <span>{t('workEnd')}:</span>
                  <strong className={!entry.end ? "waiting-text" : ""}>
                    {entry.end || t('waiting')}
                  </strong>
                </div>

                <div className="info-row">
                  <span>{t('breakTime')}:</span>
                  <strong>{entry.pause || "00:00"}</strong>
                </div>

                <div className="info-row total">
                  <span>{t('total')}:</span>
                  <strong>{calculateDayTotal(entry) ? `${calculateDayTotal(entry)} h` : "--:--"}</strong>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default WorkerMyHours;